const en = {
    seoTitle: 'About us',
    seoDescription: 'Ura means the bridge in Albanian. Meet Ura Advisory, its founder Qëndrim Uka, a former ATK tax inspector and EY tax and finance consultant, and the principles behind our work.',
    breadcrumb: 'About us',
    hero: {
        eyebrow: 'About Ura Advisory',
        title: 'The bridge between Kosovo and global markets',
        text: 'We are a tax, finance and business transformation firm in Prishtina. We connect inside knowledge of Kosovo tax and accounting with modern systems, for companies at home and businesses abroad.',
        bridgesLabel: 'Two bridges',
        bridges: [
            { left: 'Kosovo', right: 'Global markets' },
            { left: 'Tax expertise', right: 'Technology' }
        ]
    },
    name: {
        eyebrow: 'The name',
        title: 'Ura means “the bridge”',
        paragraphs: [
            'In Albanian, ura is the bridge. The name describes what we do: we connect two sides that rarely meet.',
            'The first bridge runs between Kosovo and global markets. Companies abroad get a finance team in Prishtina that works in their language, their time zone and their standards. Foreign investors get a guide through registration, tax and payroll. Kosovo companies get advice that holds up when they sell or raise money abroad.',
            'The second runs between tax expertise and technology. Judgement about the law stays with experienced people. The routine work of reading documents, building the VAT books and tracking deadlines runs through systems, so our clients see their numbers as they happen rather than at the year end.'
        ],
        imageAlt: 'A bridge over a river in a European city at dusk'
    },
    founder: {
        eyebrow: 'Founder',
        title: 'Qëndrim Uka',
        role: 'Founder, Ura Advisory',
        intro: 'Qëndrim has worked on both sides of the tax return. As a tax inspector at the Kosovo Tax Administration he examined company accounts and represented ATK before independent appeal tribunals. At EY Kosovo he prepares statutory accounts and IFRS financial statements and represents clients before Kosovo authorities. As a part-time CFO he runs planning, reporting and payroll for an international technology company.',
        facts: [
            { label: 'Languages', value: 'Albanian (native), English (C1)' },
            { label: 'Based in', value: 'Prishtina, Kosovo' },
            { label: 'Recognition', value: 'IPKO Foundation Next Generation scholarship' }
        ],
        careerTitle: 'Career',
        career: [
            {
                period: '2022 to present',
                role: 'CFO and Senior HR Officer (part time)',
                org: 'International technology company',
                text: 'FP&A, budgeting and forecasting, reporting to stakeholders and investors, accounting and tax compliance, payroll and HR.'
            },
            {
                period: '2022 to 2023',
                role: 'Management consultant',
                org: 'Kosovo business group',
                text: 'Strategy, organisational structure and operations.'
            },
            {
                period: '2021 to present',
                role: 'Senior Tax and Finance Consultant, Accounting and Global Compliance Reporting',
                org: 'EY Kosovo',
                text: 'Statutory accounts and IFRS financial statements for domestic and foreign clients, representation before Kosovo authorities, payroll and business tax compliance, management reports and KPIs. Sectors include technology, media, advanced manufacturing, energy, transportation and telecommunications.'
            },
            {
                period: '2019 to 2021',
                role: 'Tax Inspector, Tax Audit department',
                org: 'Kosovo Tax Administration (ATK)',
                text: 'Examined accounts, payroll and bank reconciliations, wrote tax audit reports, negotiated settlements and represented the tax administration before independent appeal tribunals.'
            },
            {
                period: '2019',
                role: 'Tax Compliance Assistant',
                org: 'EY Kosovo',
                text: ''
            },
            {
                period: '2018',
                role: 'Assistant to the finance manager',
                org: 'FedEx Kosovo',
                text: ''
            },
            {
                period: '2015 to 2017',
                role: 'Lecturer in financial and managerial accounting',
                org: '',
                text: ''
            }
        ],
        educationTitle: 'Education and training',
        education: [
            { title: 'BSc in Accounting', org: 'Faculty of Economics, University of Prishtina “Hasan Prishtina”', note: 'Final GPA 9.93' },
            { title: "Master's studies in Banking, Finance and Accounting", org: 'University of Prishtina', note: '' },
            { title: "Master's studies in Management", org: 'Universum College', note: '' },
            { title: 'Tax Inspector Training Academy', org: 'Kosovo Tax Administration (ATK)', note: 'Personal income tax, corporate income tax and VAT law' },
            { title: 'SeGRID summer academy', org: 'Swedish Institute, Linköping University', note: '' }
        ]
    },
    mission: {
        missionLabel: 'Mission',
        mission: 'To give every client tax and finance work that is correct, on time and explained in plain language, with the systems to see their numbers whenever they need them.',
        visionLabel: 'Vision',
        vision: 'To be the firm that companies in Kosovo and abroad think of first when they need finance done properly, and the proof that world-class finance work can be delivered from Prishtina.'
    },
    values: {
        eyebrow: 'Values',
        title: 'Six values, each with a behaviour you can check',
        items: [
            { title: 'Integrity', text: 'We tell you what the law says, even when it is not what you hoped to hear.' },
            { title: 'Precision', text: 'Every return is checked by a second person before it is filed.' },
            { title: 'Clarity', text: 'Advice comes in writing, in plain language, with the recommendation first.' },
            { title: 'Responsiveness', text: 'You get an answer within one working day, even if the answer is when we will know.' },
            { title: 'Confidentiality', text: 'Your documents are seen only by the people working on your file.' },
            { title: 'Progress', text: 'If a task repeats every month, we look for a way to automate it, and we tell you when we do.' }
        ]
    },
    principles: {
        eyebrow: 'How we work',
        title: 'Principles behind every engagement',
        items: [
            { title: 'One accountable lead', text: 'A named person owns your file, knows its history and is your first point of contact.' },
            { title: 'Four-eyes review', text: 'Nothing leaves the firm without being reviewed by a second qualified person.' },
            { title: 'Written positions', text: 'Where the law needs interpretation, we write down our position and the reasons for it.' },
            { title: 'Data protection', text: 'Client data is stored privately, shared through expiring links and handled under GDPR and Kosovo law.' },
            { title: 'Plain language', text: 'Reports explain what the numbers mean and what to do next, without jargon.' },
            { title: 'Technology with human judgement', text: 'Systems read documents and track deadlines. People make the decisions and sign off.' }
        ]
    },
    quality: {
        eyebrow: 'Quality and confidentiality',
        title: 'Standards we hold ourselves to',
        text: 'These are working standards that apply to every engagement. They are not certifications.',
        items: [
            { title: 'Engagement letters', text: 'Every engagement starts with a written scope, fees, responsibilities and terms.' },
            { title: 'Client verification', text: 'We verify companies and their owners before we start, in line with anti-money laundering rules.' },
            { title: 'Confidentiality', text: 'Client information is shared only with the people working on the file and the authorities you instruct us to deal with.' },
            { title: 'Access control', text: 'Two-factor authentication, role-based access and an audit log of every important action in the portal.' },
            { title: 'Reporting standards', text: 'Financial statements under IFRS, filings under current Kosovo tax law and ATK guidance.' },
            { title: 'AI with review', text: 'AI reads and suggests. A qualified person reviews every output before it is used, and client data is never used to train models.' }
        ]
    },
    reach: {
        eyebrow: 'Languages and markets',
        title: 'English and Albanian, from Prishtina to six markets and beyond',
        text: 'We write reports, emails and filings in the language you work in, and we schedule calls across European and US working hours.',
        facts: [
            { label: 'Working languages', value: 'English and Albanian' },
            { label: 'Time zone', value: 'Central European Time (CET and CEST)' },
            { label: 'Office', value: 'Prishtina, Kosovo' }
        ],
        marketsLabel: 'Markets',
        markets: ['Kosovo', 'United Kingdom', 'United States', 'Germany', 'Switzerland', 'Austria', 'Clients anywhere']
    },
    ctaTitle: 'Work with a firm that knows both sides'
}

const sq: typeof en = {
    seoTitle: 'Rreth nesh',
    seoDescription: 'Ura, si në shqip: lidhja mes dy brigjeve. Njihuni me Ura Advisory, me themeluesin Qëndrim Uka, ish-inspektor tatimor i ATK-së dhe konsulent për tatime dhe financa në EY, dhe me parimet e punës sonë.',
    breadcrumb: 'Rreth nesh',
    hero: {
        eyebrow: 'Rreth Ura Advisory',
        title: 'Ura mes Kosovës dhe tregjeve globale',
        text: 'Jemi një firmë tatimore, financiare dhe e transformimit të biznesit në Prishtinë. Lidhim njohjen nga brenda të tatimeve dhe kontabilitetit në Kosovë me sisteme moderne, për kompanitë vendase dhe bizneset jashtë vendit.',
        bridgesLabel: 'Dy ura',
        bridges: [
            { left: 'Kosova', right: 'Tregjet globale' },
            { left: 'Ekspertiza tatimore', right: 'Teknologjia' }
        ]
    },
    name: {
        eyebrow: 'Emri',
        title: 'Pse quhemi Ura',
        paragraphs: [
            'Ura është ajo që lidh dy brigje. Emri përshkruan atë që bëjmë: lidhim dy anë që rrallë takohen.',
            'Ura e parë shtrihet mes Kosovës dhe tregjeve globale. Kompanitë jashtë vendit marrin një ekip financiar në Prishtinë që punon në gjuhën, zonën kohore dhe standardet e tyre. Investitorët e huaj marrin një udhërrëfyes për regjistrimin, tatimet dhe pagat. Kompanitë kosovare marrin këshilla që qëndrojnë kur shesin ose sigurojnë financim jashtë vendit.',
            'E dyta shtrihet mes ekspertizës tatimore dhe teknologjisë. Gjykimi për ligjin mbetet te njerëzit me përvojë. Puna rutinë, leximi i dokumenteve, ndërtimi i librave të TVSH-së dhe ndjekja e afateve, kalon përmes sistemeve, që klientët tanë t’i shohin shifrat kur ndodhin dhe jo në fund të vitit.'
        ],
        imageAlt: 'Një urë mbi lumë në një qytet evropian në muzg'
    },
    founder: {
        eyebrow: 'Themeluesi',
        title: 'Qëndrim Uka',
        role: 'Themelues, Ura Advisory',
        intro: 'Qëndrimi ka punuar në të dyja anët e deklaratës tatimore. Si inspektor tatimor në Administratën Tatimore të Kosovës ka kontrolluar llogaritë e kompanive dhe ka përfaqësuar ATK-në para organeve të pavarura të ankesave. Në EY Kosovë përgatit pasqyra financiare statutore dhe sipas SNRF dhe përfaqëson klientët para autoriteteve të Kosovës. Si CFO me kohë të pjesshme drejton planifikimin, raportimin dhe pagat për një kompani ndërkombëtare teknologjike.',
        facts: [
            { label: 'Gjuhët', value: 'Shqip (amtare), anglisht (C1)' },
            { label: 'Me seli në', value: 'Prishtinë, Kosovë' },
            { label: 'Mirënjohje', value: 'Bursa Next Generation e Fondacionit IPKO' }
        ],
        careerTitle: 'Karriera',
        career: [
            {
                period: '2022 deri sot',
                role: 'CFO dhe zyrtar i lartë për burime njerëzore (me kohë të pjesshme)',
                org: 'Kompani ndërkombëtare teknologjike',
                text: 'FP&A, buxhetim dhe parashikim, raportim për palët e interesit dhe investitorët, përputhshmëri kontabël dhe tatimore, paga dhe burime njerëzore.'
            },
            {
                period: '2022 deri 2023',
                role: 'Konsulent menaxhmenti',
                org: 'Grup biznesi në Kosovë',
                text: 'Strategji, strukturë organizative dhe operacione.'
            },
            {
                period: '2021 deri sot',
                role: 'Konsulent i lartë për tatime dhe financa, Kontabilitet dhe Raportim Global i Përputhshmërisë',
                org: 'EY Kosovë',
                text: 'Pasqyra financiare statutore dhe sipas SNRF për klientë vendas dhe të huaj, përfaqësim para autoriteteve të Kosovës, përputhshmëri në paga dhe tatime biznesi, raporte menaxheriale dhe KPI. Sektorët përfshijnë teknologjinë, mediat, prodhimin e avancuar, energjinë, transportin dhe telekomunikacionin.'
            },
            {
                period: '2019 deri 2021',
                role: 'Inspektor tatimor, Departamenti i Kontrollit Tatimor',
                org: 'Administrata Tatimore e Kosovës (ATK)',
                text: 'Kontrolloi llogari, paga dhe rakordime bankare, hartoi raporte të kontrollit tatimor, negocioi marrëveshje dhe përfaqësoi administratën tatimore para organeve të pavarura të ankesave.'
            },
            {
                period: '2019',
                role: 'Asistent për përputhshmëri tatimore',
                org: 'EY Kosovë',
                text: ''
            },
            {
                period: '2018',
                role: 'Asistent i menaxherit të financave',
                org: 'FedEx Kosovë',
                text: ''
            },
            {
                period: '2015 deri 2017',
                role: 'Ligjërues i kontabilitetit financiar dhe menaxherial',
                org: '',
                text: ''
            }
        ],
        educationTitle: 'Arsimimi dhe trajnimet',
        education: [
            { title: 'Bachelor në Kontabilitet', org: 'Fakulteti Ekonomik, Universiteti i Prishtinës “Hasan Prishtina”', note: 'Nota mesatare përfundimtare 9,93' },
            { title: 'Studime master në Banka, Financa dhe Kontabilitet', org: 'Universiteti i Prishtinës', note: '' },
            { title: 'Studime master në Menaxhment', org: 'Kolegji Universum', note: '' },
            { title: 'Akademia e trajnimit për inspektorë tatimorë', org: 'Administrata Tatimore e Kosovës (ATK)', note: 'Ligjet për tatimin në të ardhurat personale, tatimin në të ardhurat e korporatave dhe TVSH-në' },
            { title: 'Akademia verore SeGRID', org: 'Instituti Suedez, Universiteti i Linköpingut', note: '' }
        ]
    },
    mission: {
        missionLabel: 'Misioni',
        mission: 'T’i ofrojmë çdo klienti punë tatimore dhe financiare të saktë, në kohë dhe të shpjeguar në gjuhë të thjeshtë, me sistemet për t’i parë shifrat e tij sa herë që i duhen.',
        visionLabel: 'Vizioni',
        vision: 'Të jemi firma që kompanitë në Kosovë dhe jashtë saj e kujtojnë të parën kur u duhen financa të rregullta, dhe dëshmia se puna financiare e nivelit botëror mund të ofrohet nga Prishtina.'
    },
    values: {
        eyebrow: 'Vlerat',
        title: 'Gjashtë vlera, secila me një sjellje që mund ta verifikoni',
        items: [
            { title: 'Integriteti', text: 'Ju themi çfarë thotë ligji, edhe kur nuk është ajo që shpresonit të dëgjonit.' },
            { title: 'Saktësia', text: 'Çdo deklaratë kontrollohet nga një person i dytë para se të dorëzohet.' },
            { title: 'Qartësia', text: 'Këshillat jepen me shkrim, në gjuhë të thjeshtë, me rekomandimin në fillim.' },
            { title: 'Reagimi', text: 'Merrni përgjigje brenda një dite pune, edhe kur përgjigjja është se kur do ta dimë.' },
            { title: 'Konfidencialiteti', text: 'Dokumentet tuaja i shohin vetëm njerëzit që punojnë me dosjen tuaj.' },
            { title: 'Përparimi', text: 'Nëse një detyrë përsëritet çdo muaj, kërkojmë mënyrë ta automatizojmë dhe jua themi kur e bëjmë.' }
        ]
    },
    principles: {
        eyebrow: 'Si punojmë',
        title: 'Parimet pas çdo angazhimi',
        items: [
            { title: 'Një përgjegjës i vetëm', text: 'Një person i caktuar e ka në ngarkim dosjen tuaj, e njeh historikun e saj dhe është kontakti juaj i parë.' },
            { title: 'Rishikim me katër sy', text: 'Asgjë nuk del nga firma pa u rishikuar nga një person i dytë i kualifikuar.' },
            { title: 'Qëndrime me shkrim', text: 'Kur ligji kërkon interpretim, e shkruajmë qëndrimin tonë dhe arsyet për të.' },
            { title: 'Mbrojtja e të dhënave', text: 'Të dhënat e klientëve ruhen në mënyrë private, ndahen përmes lidhjeve që skadojnë dhe trajtohen sipas GDPR-së dhe ligjit të Kosovës.' },
            { title: 'Gjuhë e thjeshtë', text: 'Raportet shpjegojnë çfarë do të thonë shifrat dhe çfarë duhet bërë më pas, pa zhargon.' },
            { title: 'Teknologji me gjykim njerëzor', text: 'Sistemet lexojnë dokumentet dhe ndjekin afatet. Njerëzit marrin vendimet dhe japin miratimin.' }
        ]
    },
    quality: {
        eyebrow: 'Cilësia dhe konfidencialiteti',
        title: 'Standardet që i zbatojmë për veten',
        text: 'Këto janë standarde pune që vlejnë për çdo angazhim. Nuk janë certifikime.',
        items: [
            { title: 'Letra angazhimi', text: 'Çdo angazhim nis me fushëveprim, tarifa, përgjegjësi dhe kushte me shkrim.' },
            { title: 'Verifikimi i klientit', text: 'I verifikojmë kompanitë dhe pronarët e tyre para se të fillojmë, në përputhje me rregullat kundër pastrimit të parave.' },
            { title: 'Konfidencialiteti', text: 'Informacioni i klientit ndahet vetëm me njerëzit që punojnë me dosjen dhe me autoritetet me të cilat na udhëzoni të komunikojmë.' },
            { title: 'Kontrolli i qasjes', text: 'Vërtetim me dy faktorë, qasje sipas roleve dhe regjistër auditimi për çdo veprim të rëndësishëm në portal.' },
            { title: 'Standardet e raportimit', text: 'Pasqyra financiare sipas SNRF, deklarata sipas legjislacionit tatimor në fuqi të Kosovës dhe udhëzimeve të ATK-së.' },
            { title: 'AI me rishikim', text: 'AI lexon dhe sugjeron. Një person i kualifikuar e rishikon çdo rezultat para se të përdoret, dhe të dhënat e klientëve nuk përdoren kurrë për të trajnuar modele.' }
        ]
    },
    reach: {
        eyebrow: 'Gjuhët dhe tregjet',
        title: 'Anglisht dhe shqip, nga Prishtina në gjashtë tregje e më gjerë',
        text: 'Raportet, emailet dhe dokumentet i shkruajmë në gjuhën në të cilën punoni dhe takimet i planifikojmë brenda orarit të punës në Evropë dhe SHBA.',
        facts: [
            { label: 'Gjuhët e punës', value: 'Anglisht dhe shqip' },
            { label: 'Zona kohore', value: 'Ora e Evropës Qendrore (CET dhe CEST)' },
            { label: 'Zyra', value: 'Prishtinë, Kosovë' }
        ],
        marketsLabel: 'Tregjet',
        markets: ['Kosova', 'Mbretëria e Bashkuar', 'Shtetet e Bashkuara', 'Gjermania', 'Zvicra', 'Austria', 'Klientë kudo']
    },
    ctaTitle: 'Punoni me një firmë që i njeh të dyja anët'
}

export const siteAbout = { en, sq }
